// src/pages/QuestionnairePage.jsx

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import companiesApi from "../../api/companiesApi";
import companyBranchesApi from "../../api/companyBranchesApi";
import { isBranchId } from "../../api/questionnaireScopeApi";
import HRA from "../questionnaires/hra";
import estresBurnout from "../questionnaires/estresLaboral";
import HealthBurnoutQuestionnaire, {
  HEALTH_BURNOUT_TOTAL_QUESTIONS
} from "../questionnaires/healthBurnoutQuestionnaire";
import Seguimiento from "../questionnaires/seguimiento";
import Nom035Questionnaire from "../questionnaires/nom035Questionnaire";
import Nom035SecIQuestionnaire from "../questionnaires/nom035SecIQuestionnaire";
import GeneralHealtQuestionarie from "../questionnaires/generalHealtQuestionarie";
import DefaultQuestionnaire from "../questionnaires/defaultQuestionnaire";
import Header from "../../components/header/header";
import nomQuestions from "../../data/nomQuestions";
import nom035SecIQuestions from "../../data/nom035SecIQuestions";
import generalHealtQuestions from "../../data/generalHealtQuestions";
import "./questionnairePage.css";

const totalQuestionsPerQuiz = {
  hra: 72,
  estresBurnout: 6,
  healthBurnout: HEALTH_BURNOUT_TOTAL_QUESTIONS,
  seguimiento: 4,
  nom035: nomQuestions.length,  // total de preguntas NOM-035
  nom035SecI: nom035SecIQuestions.length,
  generalHealtQuestionarie: generalHealtQuestions.length + 1,
  generalHealthQuestionnaire: generalHealtQuestions.length + 1,
  generalHealthQuestionarie: generalHealtQuestions.length + 1,
};

const questionnaireComponents = {
  hra: HRA,
  estresBurnout: estresBurnout,
  healthBurnout: HealthBurnoutQuestionnaire,
  seguimiento: Seguimiento,
  nom035: Nom035Questionnaire,
  nom035SecI: Nom035SecIQuestionnaire,
  generalHealtQuestionarie: GeneralHealtQuestionarie,
  generalHealthQuestionnaire: GeneralHealtQuestionarie,
  generalHealthQuestionarie: GeneralHealtQuestionarie,
};

const wideQuestionnaires = [
  "nom035",
  "nom035SecI",
  "generalHealtQuestionarie",
  "generalHealthQuestionnaire",
  "generalHealthQuestionarie",
];

const QuestionnairePage = () => {
  const { questionnaire, idEmpresa, applicationId } = useParams();
  const QuestionnaireComponent =
    questionnaireComponents[questionnaire] || DefaultQuestionnaire;
  const totalQuestions =
    totalQuestionsPerQuiz[questionnaire] || totalQuestionsPerQuiz.nom035;

  const [answeredQuestions, setAnsweredQuestions] = useState(0);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const updateProgress = () => {
    setAnsweredQuestions((prev) => Math.min(prev + 1, totalQuestions));
  };

  useEffect(() => {
    if (!idEmpresa || !questionnaire) return;
    const fetchPdfInfo = async () => {
      try {
        const response = isBranchId(idEmpresa)
          ? await companyBranchesApi.getBranchSurveyPdf(idEmpresa, questionnaire)
          : await companiesApi.getCompanySurveyPdf(idEmpresa, questionnaire);
        setPdfUrl(response.complianceURL);
      } catch {
        setPdfUrl(null);
      }
    };
    fetchPdfInfo();
  }, [idEmpresa, questionnaire]);

  // Determine the default privacy PDF path
  const defaultPrivacyPath =
    questionnaire === "nom035" || questionnaire === "nom035SecI"
      ? "/avisos/aviso_privacidad_nom.pdf"
      : "/avisos/aviso_privacidad.pdf";

  return (
    <>
      <Header progress={(answeredQuestions / totalQuestions) * 100} />

      <div className="questionnaire-page">
        <div className="side-section" />

        <div
          className={[
            "main-section",
            !acceptedPrivacy && "privacy-mode",
            wideQuestionnaires.includes(questionnaire) && "wide"
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {!acceptedPrivacy ? (
            <>
              {questionnaire === "estresBurnout" ? (
                <>
                  <h2>
                    <strong>Bienvenido a tu Cuestionario de Estrés y Burnout</strong>
                  </h2>
                  <p>
                    Este cuestionario es confidencial y te ayudará a identificar niveles
                    de estrés y señales de burnout. Antes de continuar, por favor lee el{" "}
                    <a
                      href="#"
                      className="download-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPrivacyModal(true);
                      }}
                    >
                      Aviso de Privacidad
                    </a>
                    .
                  </p>
                  <p>
                    Es breve y te tomará aproximadamente <strong>8 minutos</strong>. Responde
                    con sinceridad.
                  </p>
                </>
              ) : questionnaire === "hra" ? (
                <>
                  <h2>
                    <strong>Bienvenido a tu Evaluación de Riesgo en Salud</strong>
                  </h2>
                  <p>
                    Este cuestionario es confidencial y te ayudará a identificar factores
                    clave de tu salud integral. Antes de continuar, por favor lee el{" "}
                    <a
                      href="#"
                      className="download-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPrivacyModal(true);
                      }}
                    >
                      Aviso de Privacidad
                    </a>
                    .
                  </p>
                  <p>
                    Es breve y te tomará aproximadamente <strong>8 minutos</strong>. Responde
                    con sinceridad.
                  </p>
                </>
              ) : questionnaire === "nom035" ? (
                <>
                  <h2>
                    <strong>Cuestionario de Riesgo y Ambiente Laboral (NOM-035)</strong>
                  </h2>
                  <p>
                    Mide factores de riesgo psicosocial y condiciones del ambiente
                    laboral. Antes de continuar, por favor lee el{" "}
                    <a
                      href="#"
                      className="download-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPrivacyModal(true);
                      }}
                    >
                      Aviso de Privacidad
                    </a>
                    .
                  </p>
                  <p>
                    <strong>Importante:</strong> El progreso no se guarda. Te recomendamos
                    disponer de <strong>10 a 15 minutos</strong> para contestarlo completamente.
                  </p>
                </>
              ) : questionnaire === "nom035SecI" ? (
                <>
                  <h2>
                    <strong>Nom035 Guia I</strong>
                  </h2>
                  <p>
                    Este cuestionario evalúa acontecimientos traumáticos severos en el entorno
                    laboral. Antes de continuar, por favor lee el{" "}
                    <a
                      href="#"
                      className="download-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPrivacyModal(true);
                      }}
                    >
                      Aviso de Privacidad
                    </a>
                    .
                  </p>
                  <p>
                    <strong>Importante:</strong> Te recomendamos disponer de{" "}
                    <strong>5 a 10 minutos</strong> para contestarlo completamente.
                  </p>
                </>
              ) : questionnaire === "healthBurnout" ? (
                <>
                  <h2>
                    <strong>Bienvenido a tu Cuestionario HRA & Burnout</strong>
                  </h2>
                  <p>
                    Este cuestionario fusiona HRA con Estrés Laboral/Burnout. Primero
                    responderás HRA y luego la sección de burnout. Antes de continuar,
                    por favor lee el{" "}
                    <a
                      href="#"
                      className="download-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPrivacyModal(true);
                      }}
                    >
                      Aviso de Privacidad
                    </a>
                    .
                  </p>
                  <p>
                    Te tomará aproximadamente <strong>12 a 15 minutos</strong>.
                    Responde con sinceridad.
                  </p>
                </>
              ) : ["generalHealtQuestionarie", "generalHealthQuestionnaire", "generalHealthQuestionarie"].includes(questionnaire) ? (
                <>
                  <h2>
                    <strong>Evaluación Integral de Bienestar Emocional</strong>
                  </h2>
                  <p>
                    Este cuestionario es confidencial y te ayudara a identificar
                    indicadores generales de salud emocional. Antes de continuar,
                    por favor lee el{" "}
                    <a
                      href="#"
                      className="download-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPrivacyModal(true);
                      }}
                    >
                      Aviso de Privacidad
                    </a>
                    .
                  </p>
                  <p>
                    <strong>Importante:</strong> Te recomendamos disponer de{" "}
                    <strong>5 a 8 minutos</strong> para contestarlo completamente.
                  </p>
                </>
              ) : (
                <>
                  <h2>Aviso de Privacidad</h2>
                  <p>
                    Para continuar, por favor lee nuestro{" "}
                    <a
                      href="#"
                      className="download-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPrivacyModal(true);
                      }}
                    >
                      Aviso de Privacidad
                    </a>
                    .
                  </p>
                </>
              )}

              <button
                className="accept-button"
                onClick={() => setAcceptedPrivacy(true)}
              >
                He leído y acepto el Aviso de Privacidad
              </button>
            </>
          ) : (
            <QuestionnaireComponent
              idEmpresa={idEmpresa}
              applicationId={applicationId}
              {...(questionnaire === "healthBurnout"
                ? { totalQuestions }
                : {})}
              updateProgress={updateProgress}
            />
          )}
        </div>

        <div className="side-section" />
      </div>

      {showPrivacyModal && (
        <div className="privacy-modal">
          <div className="privacy-modal-content">
            <iframe
              src={pdfUrl || defaultPrivacyPath}
              title="Aviso de Privacidad"
            />
            <button
              className="accept-button"
              onClick={() => {
                setAcceptedPrivacy(true);
                setShowPrivacyModal(false);
              }}
            >
              He leído y acepto el Aviso de Privacidad
            </button>
            <p
              className="cancel-text"
              onClick={() => setShowPrivacyModal(false)}
            >
              Cancelar
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default QuestionnairePage;
